import React from "react";
import { Checkbox, FormControlLabel, Stack } from "@mui/material";
import CustomInput from "../forms/input/Input";
import DatePickerInput from "../forms/input/DatePickerInput";
import CustomOutlinedSelect from "../forms/select/OutlinedCustomSelect";
import CustomSwitch from "../components/switch/Switch";
import CustomInputRange from "../forms/input/InputRange";

// Define FormField type (or import it)
interface OptionType {
  value: string;
  label: string;
}

interface FormField {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
  options?: OptionType[];
  required: boolean;
  showTimeSelect?: boolean;
  disableFutureDates?: boolean;
}

export const renderTypeSpecificField = <T extends Record<string, any>>(
  field: FormField,
  formData: T,
  setFormData: React.Dispatch<React.SetStateAction<T>>,
): React.ReactNode => {
  const value = formData[field.name] || "";

  // console.log("🚀 field", field);

  switch (field.type) {
    case "text":
    case "email":
    case "number":
      return (
        <CustomInput
          key={field.name}
          label={field.label}
          placeholder={field.placeholder}
          type={field.type}
          required={field.required}
          value={value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFormData((old) => ({
              ...old,
              [field.name]: event.target.value,
            }));
          }}
        />
      );
    case "range":
      return (
        <CustomInputRange
          key={field.name}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          value={value}
          onStartChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFormData((old) => ({
              ...old,
              [field.name]: { ...old[field.name], start: event.target.value },
            }));
          }}
          onEndChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFormData((old) => ({
              ...old,
              [field.name]: { ...old[field.name], end: event.target.value },
            }));
          }}
        />
      );
    case "date":
      return (
        <DatePickerInput
          key={field.name}
          label={field.label}
          format="dd/MM/yyyy"
          placeholder={field.placeholder}
          required={field.required}
          value={value || null}
          maxDate={field.disableFutureDates ? new Date() : undefined}
          onChange={(date) => {
            setFormData((old) => ({
              ...old,
              [field.name]: date?.toUTCString() || new Date(),
            }));
          }}
          showTimeSelect={field.showTimeSelect}
        />
      );
    case "select":
      return (
        <CustomOutlinedSelect
          key={field.name}
          label={field.label}
          options={field.options || []}
          placeholder={field.placeholder}
          required={field.required}
          value={value}
          onChange={(newValue) => {
            setFormData((old) => ({
              ...old,
              [field.name]: newValue,
            }));
          }}
        />
      );
    case "checkbox":
      return (
        <FormControlLabel
          key={field.name}
          control={
            <Checkbox
              required={field.required}
              checked={Boolean(value)}
              onChange={(event) => {
                setFormData((old) => ({
                  ...old,
                  [field.name]: event.target.checked,
                }));
              }}
              sx={{
                color: "#1976d2",
                "&.Mui-checked": {
                  color: "#1976d2",
                },
              }}
            />
          }
          label={field.label}
        />
      );
    case "Checkbox":
      return (
        <Stack mt={"30px"}>
          <FormControlLabel
            key={field.name}
            control={
              <Checkbox
                required={field.required}
                checked={Boolean(value)}
                onChange={(event) => {
                  setFormData((old) => ({
                    ...old,
                    [field.name]: event.target.checked,
                  }));
                }}
                sx={{
                  color: "#1976d2",
                  "&.Mui-checked": {
                    color: "#1976d2",
                  },
                }}
              />
            }
            label={field.label}
          />
        </Stack>
      );
    case "switch":
      return (
        <Stack mt={"30px"}>
          <CustomSwitch
            key={field.name}
            label={field.label}
            value={Boolean(value)}
            onChange={(event) => {
              setFormData((old) => ({
                ...old,
                [field.name]: event.target.checked,
              }));
            }}
          />
        </Stack>
      );
    default:
      return <p key={field.name}>{field.type}</p>;
  }
};
